import React, { useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import soundEffects from '../utils/soundEffects';
import ReflectionService from '../services/ReflectionService';
import { markdownToHtml } from '../utils/markdownToHtml';
import { isInteractiveStep, countCheckpoints } from '../utils/workshopFormat';
import {
  FaArrowRight,
  FaArrowLeft,
  FaCheck,
  FaTimes,
  FaFlagCheckered,
  FaRedo,
  FaPlay,
  FaLightbulb,
  FaPenFancy
} from 'react-icons/fa';
import './LessonWorkshop.css';

const VERDICT_META = {
  good: { label: 'Strong choice', className: 'verdict-good' },
  risky: { label: 'Risky move', className: 'verdict-risky' },
  bad: { label: 'Costly move', className: 'verdict-bad' }
};

const Markdown = ({ text, className = '' }) => (
  <div
    className={`ws-markdown ${className}`}
    dangerouslySetInnerHTML={{ __html: markdownToHtml(text || '') }}
  />
);

/**
 * LessonWorkshop — interactive slide-workshop player for interactive lessons.
 *
 * Props:
 * - doc: parsed Workshop document (from parseWorkshopDoc)
 * - lessonId: lesson this workshop belongs to (used when saving reflections)
 * - onFinish: optional callback fired when the learner reaches the end screen
 */
const LessonWorkshop = ({ doc, lessonId, onFinish }) => {
  const [screen, setScreen] = useState('start'); // 'start' | 'steps' | 'end'
  const [stepIndex, setStepIndex] = useState(0);
  // Per-step interaction state: { [index]: { picked, locked, correct, value } }
  const [responses, setResponses] = useState({});

  const totalSteps = doc.steps.length;
  const checkpoints = useMemo(() => countCheckpoints(doc), [doc]);
  const step = doc.steps[stepIndex];
  const response = responses[stepIndex] || {};
  const answered = !isInteractiveStep(step) || !!response.locked;

  const score = useMemo(
    () => Object.values(responses).filter((r) => r.locked && r.correct === true).length,
    [responses]
  );

  const setResponse = (patch) => {
    setResponses((prev) => ({ ...prev, [stepIndex]: { ...(prev[stepIndex] || {}), ...patch } }));
  };

  const goNext = () => {
    soundEffects.playClick();
    if (stepIndex + 1 >= totalSteps) {
      soundEffects.playLevelUp();
      setScreen('end');
      if (onFinish) onFinish({ score, checkpoints });
    } else {
      setStepIndex(stepIndex + 1);
    }
  };

  const goBack = () => {
    soundEffects.playClick();
    if (stepIndex > 0) setStepIndex(stepIndex - 1);
  };

  const restart = () => {
    soundEffects.playClick();
    setResponses({});
    setStepIndex(0);
    setScreen('steps');
  };

  // ---------- interaction handlers ----------

  const answerQuiz = (optionIndex) => {
    if (response.locked) return;
    const correct = optionIndex === step.answer;
    if (correct) soundEffects.playSuccess();
    else soundEffects.playError();
    setResponse({ picked: optionIndex, locked: true, correct });
  };

  const answerPoll = (optionIndex) => {
    if (response.locked) return;
    soundEffects.playCoin();
    setResponse({ picked: optionIndex, locked: true });
  };

  const answerScenario = (choiceIndex) => {
    if (response.locked) return;
    const verdict = step.choices[choiceIndex].verdict;
    if (verdict === 'good') soundEffects.playSuccess();
    else if (verdict === 'bad') soundEffects.playError();
    else soundEffects.playCoin();
    setResponse({ picked: choiceIndex, locked: true });
  };

  const saveReflection = async () => {
    if (response.locked || response.saving) return;
    const text = (response.value || '').trim();
    if (!text) {
      toast.error('Write a sentence or two first');
      return;
    }
    setResponse({ saving: true });
    try {
      await ReflectionService.save({
        lessonId,
        bioKey: step.bioKey,
        bioLabel: step.bioLabel,
        question: step.prompt,
        answer: text,
        isGoal: step.isGoal === true
      });
      soundEffects.playSuccess();
      setResponse({ value: text, locked: true, saving: false });
      toast.success('Saved to your Money Bio');
    } catch (error) {
      setResponse({ saving: false });
      toast.error('Could not save — check your connection and try again');
    }
  };

  const lockEstimate = () => {
    if (response.locked) return;
    const value = Number.isFinite(response.value)
      ? response.value
      : Math.round((step.min + step.max) / 2);
    const tolerance = Number.isFinite(step.tolerance)
      ? step.tolerance
      : (step.max - step.min) * 0.1;
    const correct = Math.abs(value - step.answer) <= tolerance;
    if (correct) soundEffects.playSuccess();
    else soundEffects.playCoin();
    setResponse({ value, locked: true, correct });
  };

  // ---------- screens ----------

  if (screen === 'start') {
    return (
      <div className="workshop">
        <div className="ws-card ws-start">
          <div className="ws-badge">Interactive Workshop</div>
          <h2>{doc.title}</h2>
          {doc.intro && <p className="ws-intro">{doc.intro}</p>}
          <div className="ws-start-meta">
            <span>{totalSteps} steps</span>
            <span>•</span>
            <span>{checkpoints} checkpoints</span>
            <span>•</span>
            <span>~{Math.max(3, Math.round(totalSteps * 0.75))} min</span>
          </div>
          <button
            className="ws-btn ws-btn-primary"
            onClick={() => {
              soundEffects.playClick();
              setScreen('steps');
            }}
          >
            <FaPlay /> Start Workshop
          </button>
        </div>
      </div>
    );
  }

  if (screen === 'end') {
    const outro = doc.outro || {};
    return (
      <div className="workshop">
        <div className="ws-card ws-end">
          <div className="ws-end-emoji">🏁</div>
          <h2>{outro.title || 'Workshop complete!'}</h2>
          {checkpoints > 0 && (
            <div className="ws-score">
              You nailed <strong>{score}</strong> of <strong>{checkpoints}</strong> checkpoints
            </div>
          )}
          {outro.body && <Markdown text={outro.body} className="ws-outro-body" />}
          <div className="ws-end-actions">
            <button className="ws-btn ws-btn-secondary" onClick={restart}>
              <FaRedo /> Run It Again
            </button>
          </div>
          <p className="ws-end-hint">
            Don't forget to mark the lesson complete below to collect your XP.
          </p>
        </div>
      </div>
    );
  }

  // ---------- step screen ----------

  const progressPct = Math.round(((stepIndex + 1) / totalSteps) * 100);

  return (
    <div className="workshop">
      <div className="ws-progress">
        <div className="ws-progress-bar">
          <div className="ws-progress-fill" style={{ width: `${progressPct}%` }} />
        </div>
        <span className="ws-progress-label">
          {stepIndex + 1} / {totalSteps}
        </span>
      </div>

      <div className="ws-card ws-step" key={stepIndex}>
        {step.type === 'slide' && (
          <div className="ws-slide">
            {step.kicker && <div className="ws-kicker">{step.kicker}</div>}
            {step.emoji && <div className="ws-slide-emoji">{step.emoji}</div>}
            {step.title && <h3>{step.title}</h3>}
            {step.body && <Markdown text={step.body} />}
          </div>
        )}

        {step.type === 'quiz' && (
          <div className="ws-quiz">
            <div className="ws-kicker">Checkpoint</div>
            <h3>{step.question}</h3>
            <div className="ws-options">
              {step.options.map((opt, i) => {
                let cls = 'ws-option';
                if (response.locked) {
                  if (i === step.answer) cls += ' ws-option-correct';
                  else if (i === response.picked) cls += ' ws-option-wrong';
                  else cls += ' ws-option-dim';
                }
                return (
                  <button key={i} className={cls} onClick={() => answerQuiz(i)} disabled={response.locked}>
                    <span className="ws-option-letter">{String.fromCharCode(65 + i)}</span>
                    <span>{opt}</span>
                    {response.locked && i === step.answer && <FaCheck className="ws-option-icon" />}
                    {response.locked && i === response.picked && i !== step.answer && (
                      <FaTimes className="ws-option-icon" />
                    )}
                  </button>
                );
              })}
            </div>
            {response.locked && (
              <div className={`ws-feedback ${response.correct ? 'ws-feedback-good' : 'ws-feedback-bad'}`}>
                <strong>{response.correct ? 'Correct!' : 'Not quite.'}</strong>
                {step.explain && <Markdown text={step.explain} />}
              </div>
            )}
          </div>
        )}

        {step.type === 'poll' && (
          <div className="ws-poll">
            <div className="ws-kicker">Your call</div>
            <h3>{step.question}</h3>
            <div className="ws-options">
              {step.options.map((opt, i) => {
                let cls = 'ws-option';
                if (response.locked) {
                  cls += i === response.picked ? ' ws-option-picked' : ' ws-option-dim';
                }
                return (
                  <button key={i} className={cls} onClick={() => answerPoll(i)} disabled={response.locked}>
                    <span className="ws-option-letter">{String.fromCharCode(65 + i)}</span>
                    <span>{opt}</span>
                  </button>
                );
              })}
            </div>
            {response.locked && step.reveal && (
              <div className="ws-feedback ws-feedback-neutral">
                <FaLightbulb className="ws-feedback-icon" />
                <Markdown text={step.reveal} />
              </div>
            )}
          </div>
        )}

        {step.type === 'scenario' && (
          <div className="ws-scenario">
            <div className="ws-kicker">Scenario</div>
            <Markdown text={step.prompt} className="ws-scenario-prompt" />
            <div className="ws-options">
              {step.choices.map((choice, i) => {
                let cls = 'ws-option';
                if (response.locked) {
                  cls += i === response.picked ? ' ws-option-picked' : ' ws-option-dim';
                }
                return (
                  <button key={i} className={cls} onClick={() => answerScenario(i)} disabled={response.locked}>
                    <span className="ws-option-letter">{String.fromCharCode(65 + i)}</span>
                    <span>{choice.label}</span>
                  </button>
                );
              })}
            </div>
            {response.locked && (() => {
              const chosen = step.choices[response.picked];
              const meta = VERDICT_META[chosen.verdict] || VERDICT_META.risky;
              return (
                <div className={`ws-feedback ws-outcome ${meta.className}`}>
                  <div className="ws-verdict">{meta.label}</div>
                  <Markdown text={chosen.outcome} />
                </div>
              );
            })()}
          </div>
        )}

        {step.type === 'reflect' && (
          <div className="ws-reflect">
            <div className="ws-kicker">
              <FaPenFancy style={{ marginRight: 6 }} />
              Make it yours
            </div>
            <h3>{step.prompt}</h3>
            <textarea
              className="ws-reflect-input"
              value={response.value || ''}
              onChange={(e) => setResponse({ value: e.target.value })}
              placeholder={step.placeholder || 'Write a sentence or two…'}
              rows={4}
              maxLength={2000}
              disabled={response.locked || response.saving}
            />
            {!response.locked ? (
              <button
                className="ws-btn ws-btn-primary ws-lock-btn"
                onClick={saveReflection}
                disabled={response.saving}
              >
                {response.saving ? 'Saving…' : 'Save to My Money Bio'}
              </button>
            ) : (
              <div className="ws-feedback ws-feedback-good">
                <strong>Saved to your Money Bio ✓</strong>
                Your answer lives on your profile now — and you can update it any time by redoing this workshop.
              </div>
            )}
          </div>
        )}

        {step.type === 'estimate' && (
          <div className="ws-estimate">
            <div className="ws-kicker">Take a guess</div>
            <h3>{step.question}</h3>
            <div className="ws-estimate-value">
              {(Number.isFinite(response.value) ? response.value : Math.round((step.min + step.max) / 2))}
              {step.unit ? ` ${step.unit}` : ''}
            </div>
            <input
              type="range"
              min={step.min}
              max={step.max}
              step={step.step || 1}
              value={Number.isFinite(response.value) ? response.value : Math.round((step.min + step.max) / 2)}
              onChange={(e) => setResponse({ value: parseFloat(e.target.value) })}
              disabled={response.locked}
              className="ws-slider"
            />
            <div className="ws-slider-range">
              <span>{step.min}{step.unit ? ` ${step.unit}` : ''}</span>
              <span>{step.max}{step.unit ? ` ${step.unit}` : ''}</span>
            </div>
            {!response.locked ? (
              <button className="ws-btn ws-btn-primary ws-lock-btn" onClick={lockEstimate}>
                Lock It In
              </button>
            ) : (
              <div className={`ws-feedback ${response.correct ? 'ws-feedback-good' : 'ws-feedback-neutral'}`}>
                <strong>
                  {response.correct ? 'Great instincts — ' : ''}
                  The answer: {step.answer}{step.unit ? ` ${step.unit}` : ''}.
                </strong>
                {step.reveal && <Markdown text={step.reveal} />}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="ws-nav">
        <button className="ws-btn ws-btn-secondary" onClick={goBack} disabled={stepIndex === 0}>
          <FaArrowLeft /> Back
        </button>
        <button className="ws-btn ws-btn-primary" onClick={goNext} disabled={!answered}>
          {stepIndex + 1 >= totalSteps ? (
            <>
              Finish <FaFlagCheckered />
            </>
          ) : (
            <>
              Next <FaArrowRight />
            </>
          )}
        </button>
      </div>
      {!answered && <p className="ws-nav-hint">Make your choice to continue</p>}
    </div>
  );
};

export default LessonWorkshop;
